import { DateTime } from "luxon"
import {
  clearAuthKeysFromLocalstorage,
  getAccessTokenExpiresInFromLocalstorage,
  getAccessTokenFromLocalstorage,
  getIsPasswordStillDefaultFromLocalstorage,
  getRefreshTokenExpiresInFromLocalstorage,
  getRefreshTokenFromLocalstorage,
  getUserDataFromLocalstorage,
  setAccessTokenExpiresInToLocalstorage,
  setAccessTokenToLocalstorage,
  setIsPasswordStillDefaultToLocalstorage,
  setRefreshTokenExpiresInToLocalstorage,
  setRefreshTokenToLocalstorage,
  setUserDataToLocalstorage
} from "./localStorage"
import { routerPaths } from "router/routes/paths"
import { emitRefreshOrAccessTokenChangedEvent } from "./context/Token"
import DEBUG from "./IsDebugOn"
import ApiV2 from "./ApiV2"
import { AuthApi } from "services/api-routes"
import { Badge } from "reactstrap"
import IsEmpty from "./IsEmpty"

// ** Checks if an object is empty (returns boolean)
export const isObjEmpty = obj => Object.keys(obj).length === 0

// ** Returns K format from a number
export const kFormatter = num => (num > 999 ? `${(num / 1000).toFixed(1)}k` : num)

// ** Converts HTML to string
export const htmlToString = html => html.replace(/<\/?[^>]+(>|$)/g, '')

// ** Converts a string to PascalCase (e.g. "tabela-1" -> "Tabela1")
export const toPascalCase = (str) => {
  if (!str) return ''
  return str
    .toString()
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('')
}

// ** Checks if the passed date is today
const isToday = date => {
  const today = new Date()
  return (
    /* eslint-disable operator-linebreak */
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
    /* eslint-enable */
  )
}

/**
 ** Format and return date in Humanize format
 ** Intl docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/format
 ** Intl Constructor: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
 * @param {String} value date to format
 * @param {Object} formatting Intl object to format with
 */
export const formatDate = (value, formatting = { month: 'short', day: 'numeric', year: 'numeric' }) => {
  if (!value) return value
  return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value))
}

export const formatDateDefault = (dateInput, format = "dd/MM/yyyy") => {
  // Caso seja DATE
  if (dateInput instanceof Date) return DateTime.fromJSDate(dateInput).toFormat(format)

  // Caso seja ISO
  const fromISO = DateTime.fromISO(dateInput)
  if (fromISO.isValid) return fromISO.toFormat(format)

  // Caso seja string que o JS reconhece como data (ex: "Thu Sep 10 1964 ...")
  const fromJS = DateTime.fromJSDate(new Date(dateInput))
  if (fromJS.isValid) return fromJS.toFormat(format)

  // Caso não seja nada, retorna a data atual (igual o momentjs)
  return DateTime.now().setZone('system').setLocale('pt-BR').toFormat(format)
}

// ** Returns short month of passed date
export const formatDateToMonthShort = (value, toTimeForCurrentDay = true) => {
  const date = new Date(value)
  let formatting = { month: 'short', day: 'numeric' }

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = { hour: 'numeric', minute: 'numeric' }
  }

  return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value))
}

export const downloadFilesBase64 = (base, filename) => {
  const downloadLink = document.createElement("a")
  downloadLink.href = base
  downloadLink.download = filename
  downloadLink.click()
}

export const openFileInNewTab = (base64, fileName) => {
  if (!base64) return
  const mimeType = base64.substring(base64.indexOf(":") + 1, base64.indexOf(";"))

  const isImage = mimeType.startsWith("image/")
  const isPdf = mimeType === "application/pdf"
  const isHtml = mimeType === "text/html"

  const byteCharacters = atob(base64.split(",")[1])
  const byteNumbers = Array.from(byteCharacters, c => c.charCodeAt(0))
  const byteArray = new Uint8Array(byteNumbers)

  const blob = new Blob([byteArray], { type: mimeType })
  const blobUrl = URL.createObjectURL(blob)

  const win = window.open()
  if (!win) {
    URL.revokeObjectURL(blobUrl)
    return
  }

  if (fileName) win.document.title = fileName

  if (isImage) {
    win.document.write(`<img src="${blobUrl}" style="width:100%">`)
  } else if (isPdf) {
    win.location.href = blobUrl
  } else if (isHtml) {
    const htmlDecoded = new TextDecoder().decode(byteArray)
    win.document.open()
    win.document.write(htmlDecoded)
    win.document.close()
  } else {
    win.document.write("Arquivo não suportado.")
  }
}

export const handleBlobFile = (blob, fileName) => {
  if (!blob) return
  const url = URL.createObjectURL(blob)
  const isPdf = blob.type === "application/pdf"
  if (isPdf) {
    window.open(url)
  } else {
    const a = document.createElement("a")
    a.href = url
    a.download = fileName
    a.click()
  }
}

export function isPromise(value) {
  return value instanceof Promise
}

export const generateGUID = () => {
  let result = ''
  const characters = '0123456789abcdef'
  for (let i = 0; i < 32; i++) {
    if (i === 8 || i === 12 || i === 16 || i === 20) {
      result += '-'
    }
    result += characters.charAt(Math.floor(Math.random() * characters.length))
  }
  return result
}

export const generateGUIDWithSeed = (seed) => {
  function seededRandom(seed) {
    const x = Math.sin(seed) * 10000
    return x - Math.floor(x)
  }

  function s4(seed) {
    return Math.floor((1 + seededRandom(seed)) * 0x10000)
      .toString(16)
      .substring(1)
  }

  // Incrementando a semente para cada parte do GUID
  const part1 = s4(seed)
  const part2 = s4(seed + 1)
  const part3 = s4(seed + 2)
  const part4 = s4(seed + 3)
  const part5 = s4(seed + 4) + s4(seed + 5) + s4(seed + 6)

  // Construindo o GUID com template literals
  return `${part1}-${part2}-${part3}-${part4}-${part5}`
}

/**
 ** Return if user is logged in
 ** This is completely up to you and how you want to store the token in your frontend application
 *  ? e.g. If you are using cookies to store the application please update this function
 */
export const isUserLoggedIn = () => getUserDataFromLocalstorage()

export const getUserData = () => getUserDataFromLocalstorage()

export const getIsFirstPassword = () => getIsPasswordStillDefaultFromLocalstorage()

export const setIsDefaultPasswordStorage = setIsPasswordStillDefaultToLocalstorage

export const getToken = () => getAccessTokenFromLocalstorage()

export const getRefreshToken = () => getRefreshTokenFromLocalstorage()

export const getAccessTokenExpiration = () => {
  return DateTime.fromISO(getAccessTokenExpiresInFromLocalstorage())
}

export const secondsToFutureISODate = (seconds) => DateTime.now().plus({ seconds }).toISO()

export const getRefreshTokenExpiration = () => {
  return DateTime.fromISO(getRefreshTokenExpiresInFromLocalstorage())
}

export const isAccessTokenValid = () => {
  const expirationDate = getAccessTokenExpiration()

  return (
    expirationDate?.isValid
    && DateTime.now().toUnixInteger() < expirationDate.toUnixInteger()
  )
}

export const isTokenExpired = () => !isAccessTokenValid()

export const isRefreshTokenEmpty = () => {
  return IsEmpty(getRefreshTokenFromLocalstorage())
}

export function isRefreshTokenValid() {
  const expirationDate = getRefreshTokenExpiration()
  return (
    expirationDate?.isValid
    && DateTime.now().toUnixInteger() < expirationDate.toUnixInteger()
    && !isRefreshTokenEmpty()
  )
}

export const isRefreshTokenExpired = () => !isRefreshTokenValid()

export const setLocalstorageAuthKeys = data => {
  const accessTokenExpiresIn = data?.accessTokenExpiresIn ? data.accessTokenExpiresIn : data.expiresIn
  setUserDataToLocalstorage(data.userData)
  setAccessTokenToLocalstorage(data.accessToken)
  setRefreshTokenToLocalstorage(data.refreshToken)
  setAccessTokenExpiresInToLocalstorage(accessTokenExpiresIn)
  setRefreshTokenExpiresInToLocalstorage(data.refreshTokenExpiresIn)
  setIsPasswordStillDefaultToLocalstorage(data.isPasswordStillDefault)
  emitRefreshOrAccessTokenChangedEvent()
}

export const clearLocalstorageAuthKeys = () => {
  clearAuthKeysFromLocalstorage()
  emitRefreshOrAccessTokenChangedEvent()
}

export const getAuthTokenAuthorization = () => {
  const userData = getUserDataFromLocalstorage()
  return userData?.authTokenAuthorization
}

export const classificacaoOptions = [
  { value: 'O', label: 'Consultório' },
  { value: 'C', label: 'Clínica' },
  { value: 'H', label: 'Hospital' },
  { value: 'L', label: 'Laboratório' },
  { value: 'I', label: 'Centro de Imagem' },
  { value: 'P', label: 'Parceiro B2B' }
]

export const uf_conselhos_options = [
  { label: "AC", value: "AC" },
  { label: "AL", value: "AL" },
  { label: "AM", value: "AM" },
  { label: "AP", value: "AP" },
  { label: "BA", value: "BA" },
  { label: "CE", value: "CE" },
  { label: "DF", value: "DF" },
  { label: "ES", value: "ES" },
  { label: "GO", value: "GO" },
  { label: "MA", value: "MA" },
  { label: "MG", value: "MG" },
  { label: "MS", value: "MS" },
  { label: "MT", value: "MT" },
  { label: "PA", value: "PA" },
  { label: "PB", value: "PB" },
  { label: "PE", value: "PE" },
  { label: "PI", value: "PI" },
  { label: "PR", value: "PR" },
  { label: "RJ", value: "RJ" },
  { label: "RN", value: "RN" },
  { label: "RO", value: "RO" },
  { label: "RR", value: "RR" },
  { label: "RS", value: "RS" },
  { label: "SC", value: "SC" },
  { label: "SE", value: "SE" },
  { label: "SP", value: "SP" },
  { label: "TO", value: "TO" }
]

export const regimes_atendimento = [
  { label: "Ambulatorial", value: "01" },
  { label: "Domiciliar", value: "02" },
  { label: "Internação", value: "03" },
  { label: "Pronto Socorro", value: "04" },
  { label: "Telesaúde", value: "05" }
]

export const retornaAjusteDataNascimento = (datanascimento) => {
  if (datanascimento === undefined || datanascimento === null || datanascimento === '') {
    return null
  } else {
    const parts = datanascimento.split("/")
    return new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]))
  }
}

/**
 ** This function is used for demo purpose route navigation
 ** In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 ** Please note role field is just for showing purpose it's not used by anything in frontend
 ** We are checking role just for ease
 * ? NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userRole Role of user
 */
export const getHomeRouteForLoggedInUser = userRole => {
  if (userRole === 'client') return '/home'
  if (userRole === 'supervisor') return '/supervisor/home'
  if (userRole === 'operador') return '/operador/home'
  return '/login'
}

export const addDashBrazilianPhone = (phone) => {
  return phone.slice(0, phone.length - 4) + "-" + phone.slice(phone.length - 4)
}

// ** React Select Theme Colors
export const selectThemeColors = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: '#7367f01a', // for option hover bg-color
    primary: '#7367f0', // for selected option bg-color
    neutral10: '#7367f0', // for tags bg-color
    neutral20: '#ededed', // for input border-color
    neutral30: '#ededed' // for input hover border-color
  },
  spacing: {
    ...theme.spacing,
    fontSize: '14px'
  }
})

export function resetTelefoneFormat(telefone) {
  return telefone.replace(/\D/g, '')
}

export function resetCepFormat(cep) {
  return cep?.replace('.', '').replace('-', '')
}

export function resetCpfCnpjFormat(value) {
  return value?.replace(/[./\-\s]/g, '')
}

export function formatHiddenEmail(email) {
  const [user, domain] = email.split('@')
  const visibleLength = Math.min(3, user.length)
  const visible = user.slice(0, visibleLength)
  const hidden = '*'.repeat(user.length - visibleLength)
  return `${visible}${hidden}@${domain}`
}

export function formatHiddenPhoneNumber(phone) {
  return `${phone.slice(-4)}`
}

export const getCep = async cep => {
  const raw_cep = resetCepFormat(cep)

  if (raw_cep.length >= 8) {
    try {
      const reqParams = {
        method: 'GET'
      }
      const url_completa = new URL(`https://viacep.com.br/ws/${raw_cep}/json/`).toString()

      const resp = await fetch(url_completa, reqParams)
      const json = await resp.json()
      return json
    } catch (error) {
      console.log('error', error)
    }
  }
}

const api = new ApiV2()

window.__refreshTokenManagement = window.__refreshTokenManagement || {
  refreshTokenPromise: null
}

export const refreshAccessToken = async (onFailed) => {
  if (isPromise(window.__refreshTokenManagement.refreshTokenPromise)) {
    try {
      await window.__refreshTokenManagement.refreshTokenPromise
      return true
    } catch (error) {
      DEBUG() && console.error('Refresh em andamento falhou:', error)
      return false
    }
  }

  window.__refreshTokenManagement.refreshTokenPromise = new Promise(async (resolve, reject) => {
    const cbError = async (err) => {
      reject(err)
      if (onFailed && typeof onFailed === 'function') {
        clearLocalstorageAuthKeys()
        await onFailed()
      } else {
        clearLocalstorageAuthKeys()
        window.location.href = routerPaths.auth.login
        DEBUG() && console.error('Erro ao renovar token:', err)
      }
    }
    const body = {
      refreshToken: getRefreshToken()
    }

    const resp = await api.post(
      AuthApi.session.refreshToken,
      [],
      body,
      () => { },
      "",
      "",
      cbError
    )

    if (resp?.sucesso) {
      const data = resp.data

      setLocalstorageAuthKeys({
        ...data,
        accessTokenExpiresIn: secondsToFutureISODate(data.accessTokenExpiresIn),
        refreshTokenExpiresIn: secondsToFutureISODate(data.refreshTokenExpiresIn)
      })

      emitRefreshOrAccessTokenChangedEvent()
      resolve(data)
    } else {
      reject()
    }
  })

  try {
    await window.__refreshTokenManagement.refreshTokenPromise
    return true
  } catch (error) {
    DEBUG() && console.error('Falha no refresh token:', error)
    return false
  } finally {
    window.__refreshTokenManagement.refreshTokenPromise = null
  }
}

export function removerCaracteres(valor) {
  return valor
    ? String(valor)
      .replace(/[^\w\s]/gi, "")
      .trim()
    : valor
}

export function formatarValorSalvar(valor) {
  return valor
    ? parseFloat(
      String(valor)
        ?.replace(/[^0-9,.]/g, "")
        ?.replace(".", "")
        ?.replace(",", ".")
        ?.trim()
    )
    : valor
}

export function formataPercentual(valor, casasDecimais = 2) {
  return `${valor?.toLocaleString("pt-br", {
    currency: "BRL",
    maximumFractionDigits: casasDecimais
  })}%`
}

export const formatCurrency = (value, precision = 2) => {
  let translation = localStorage.getItem('translation')
  let currency = 'BRL'

  if (translation) {
    translation = translation.replace('_', '-')
    if (translation === 'en') currency = 'USD'
    else if (translation === 'es') currency = 'EUR'
  } else translation = 'pt-BR'

  return new Intl.NumberFormat(translation, {
    style: 'currency',
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    currency
  }).format(value)
}

export const findIndexInArrayById = (list, reference, param) => {
  let index = -1
  for (let i = 0; i < list.length; i++) {
    if (list[i] && list[i][reference] === param) {
      index = i
      break
    }
  }
  return index
}

export const phoneMasks = {
  AC: "+247-####",
  AD: "+376-###-###",
  AE: "+971-5#-###-####",
  AE: "+971-#-###-####",
  AF: "+93-##-###-####",
  AG: "+1(268)###-####",
  AI: "+1(264)###-####",
  AL: "+355(###)###-###",
  AM: "+374-##-###-###",
  AN: "+599-###-####",
  AN: "+599-###-####",
  AN: "+599-9###-####",
  AO: "+244(###)###-###",
  AQ: "+672-1##-###",
  AR: "+54(###)###-####",
  AS: "+1(684)###-####",
  AT: "+43(###)###-####",
  AU: "+61-#-####-####",
  AW: "+297-###-####",
  AZ: "+994-##-###-##-##",
  BA: "+387-##-#####",
  BA: "+387-##-####",
  BB: "+1(246)###-####",
  BD: "+880-##-###-###",
  BE: "+32(###)###-###",
  BF: "+226-##-##-####",
  BG: "+359(###)###-###",
  BH: "+973-####-####",
  BI: "+257-##-##-####",
  BJ: "+229-##-##-####",
  BM: "+1(441)###-####",
  BN: "+673-###-####",
  BO: "+591-#-###-####",
  BR: "+55(##) ####-####",
  BR: "+55(##) 7###-####",
  BR: "+55(##) 9####-####",
  BS: "+1(242)###-####",
  BT: "+975-17-###-###",
  BT: "+975-#-###-###",
  BW: "+267-##-###-###",
  BY: "+375(##)###-##-##",
  BZ: "+501-###-####",
  CA: "+1(###)###-####",
  CD: "+243(###)###-###",
  CF: "+236-##-##-####",
  CG: "+242-##-###-####",
  CH: "+41-##-###-####",
  CI: "+225-##-###-###",
  CK: "+682-##-###",
  CL: "+56-#-####-####",
  CM: "+237-####-####",
  CN: "+86(###)####-####",
  CN: "+86(###)####-###",
  CN: "+86-##-#####-#####",
  CO: "+57(###)###-####",
  CR: "+506-####-####",
  CU: "+53-#-###-####",
  CV: "+238(###)##-##",
  CW: "+599-###-####",
  CY: "+357-##-###-###",
  CZ: "+420(###)###-###",
  DE: "+49(####)###-####",
  DE: "+49(###)###-####",
  DE: "+49(###)##-####",
  DE: "+49(###)##-###",
  DE: "+49(###)##-##",
  DE: "+49-###-###",
  DJ: "+253-##-##-##-##",
  DK: "+45-##-##-##-##",
  DM: "+1(767)###-####",
  DO: "+1(809)###-####",
  DO: "+1(829)###-####",
  DO: "+1(849)###-####",
  DZ: "+213-##-###-####",
  EC: "+593-##-###-####",
  EC: "+593-#-###-####",
  EE: "+372-####-####",
  EE: "+372-###-####",
  EG: "+20(###)###-####",
  ER: "+291-#-###-###",
  ES: "+34(###)###-###",
  ET: "+251-##-###-####",
  FI: "+358(###)###-##-##",
  FJ: "+679-##-#####",
  FK: "+500-#####",
  FM: "+691-###-####",
  FO: "+298-###-###",
  FR: "+262-#####-####",
  FR: "+33(###)###-###",
  FR: "+508-##-####",
  FR: "+590(###)###-###",
  GA: "+241-#-##-##-##",
  GD: "+1(473)###-####",
  GE: "+995(###)###-###",
  GF: "+594-#####-####",
  GH: "+233(###)###-###",
  GI: "+350-###-#####",
  GL: "+299-##-##-##",
  GM: "+220(###)##-##",
  GN: "+224-##-###-###",
  GQ: "+240-##-###-####",
  GR: "+30(###)###-####",
  GT: "+502-#-###-####",
  GU: "+1(671)###-####",
  GW: "+245-#-######",
  GY: "+592-###-####",
  HK: "+852-####-####",
  HN: "+504-####-####",
  HR: "+385-##-###-###",
  HT: "+509-##-##-####",
  HU: "+36(###)###-###",
  ID: "+62(8##)###-####",
  ID: "+62-##-###-##",
  ID: "+62-##-###-###",
  ID: "+62-##-###-####",
  ID: "+62(8##)###-###",
  ID: "+62(8##)###-##-###",
  IE: "+353(###)###-###",
  IL: "+972-5#-###-####",
  IL: "+972-#-###-####",
  IN: "+91(####)###-###",
  IO: "+246-###-####",
  IQ: "+964(###)###-####",
  IR: "+98(###)###-####",
  IS: "+354-###-####",
  IT: "+39(###)####-###",
  JM: "+1(876)###-####",
  JO: "+962-#-####-####",
  JP: "+81-##-####-####",
  JP: "+81(###)###-###",
  KE: "+254-###-######",
  KG: "+996(###)###-###",
  KH: "+855-##-###-###",
  KI: "+686-##-###",
  KM: "+269-##-#####",
  KN: "+1(869)###-####",
  KP: "+850-191-###-####",
  KP: "+850-##-###-###",
  KP: "+850-###-####-###",
  KP: "+850-###-###",
  KP: "+850-####-####",
  KP: "+850-####-#############",
  KR: "+82-##-###-####",
  KW: "+965-####-####",
  KY: "+1(345)###-####",
  KZ: "+7(6##)###-##-##",
  KZ: "+7(7##)###-##-##",
  LA: "+856(20##)###-###",
  LA: "+856-##-###-###",
  LB: "+961-##-###-###",
  LB: "+961-#-###-###",
  LC: "+1(758)###-####",
  LI: "+423(###)###-####",
  LK: "+94-##-###-####",
  LR: "+231-##-###-###",
  LS: "+266-#-###-####",
  LT: "+370(###)##-###",
  LU: "+352(###)###-###",
  LV: "+371-##-###-###",
  LY: "+218-##-###-###",
  LY: "+218-21-###-####",
  MA: "+212-##-####-###",
  MC: "+377(###)###-###",
  MC: "+377-##-###-###",
  MD: "+373-####-####",
  ME: "+382-##-###-###",
  MG: "+261-##-##-#####",
  MH: "+692-###-####",
  MK: "+389-##-###-###",
  ML: "+223-##-##-####",
  MM: "+95-##-###-###",
  MM: "+95-#-###-###",
  MM: "+95-###-###",
  MN: "+976-##-##-####",
  MO: "+853-####-####",
  MP: "+1(670)###-####",
  MQ: "+596(###)##-##-##",
  MR: "+222-##-##-####",
  MS: "+1(664)###-####",
  MT: "+356-####-####",
  MU: "+230-###-####",
  MV: "+960-###-####",
  MW: "+265-1-###-###",
  MW: "+265-#-####-####",
  MX: "+52(###)###-####",
  MX: "+52-##-##-####",
  MY: "+60-##-###-####",
  MY: "+60(###)###-###",
  MY: "+60-##-###-###",
  MY: "+60-#-###-###",
  MZ: "+258-##-###-###",
  NA: "+264-##-###-####",
  NC: "+687-##-####",
  NE: "+227-##-##-####",
  NF: "+672-3##-###",
  NG: "+234(###)###-####",
  NG: "+234-##-###-###",
  NG: "+234-##-###-##",
  NG: "+234(###)###-####",
  NI: "+505-####-####",
  NL: "+31-##-###-####",
  NO: "+47(###)##-###",
  NP: "+977-##-###-###",
  NR: "+674-###-####",
  NU: "+683-####",
  NZ: "+64(###)###-###",
  NZ: "+64-##-###-###",
  NZ: "+64(###)###-####",
  OM: "+968-##-###-###",
  PA: "+507-###-####",
  PE: "+51(###)###-###",
  PF: "+689-##-##-##",
  PG: "+675(###)##-###",
  PH: "+63(###)###-####",
  PK: "+92(###)###-####",
  PL: "+48(###)###-###",
  PS: "+970-##-###-####",
  PT: "+351-##-###-####",
  PW: "+680-###-####",
  PY: "+595(###)###-###",
  QA: "+974-####-####",
  RE: "+262-#####-####",
  RO: "+40-##-###-####",
  RS: "+381-##-###-####",
  RU: "+7(###)###-##-##",
  RW: "+250(###)###-###",
  SA: "+966-5-####-####",
  SA: "+966-#-###-####",
  SB: "+677-###-####",
  SB: "+677-#####",
  SC: "+248-#-###-###",
  SD: "+249-##-###-####",
  SE: "+46-##-###-####",
  SG: "+65-####-####",
  SH: "+290-####",
  SH: "+290-####",
  SI: "+386-##-###-###",
  SK: "+421(###)###-###",
  SL: "+232-##-######",
  SM: "+378-####-######",
  SN: "+221-##-###-####",
  SO: "+252-##-###-###",
  SO: "+252-#-###-###",
  SO: "+252-#-###-###",
  SR: "+597-###-####",
  SR: "+597-###-###",
  SS: "+211-##-###-####",
  ST: "+239-##-#####",
  SV: "+503-##-##-####",
  SX: "+1(721)###-####",
  SY: "+963-##-####-###",
  SZ: "+268-##-##-####",
  TC: "+1(649)###-####",
  TD: "+235-##-##-##-##",
  TG: "+228-##-###-###",
  TH: "+66-##-###-####",
  TH: "+66-##-###-###",
  TJ: "+992-##-###-####",
  TK: "+690-####",
  TL: "+670-###-####",
  TL: "+670-77#-#####",
  TL: "+670-78#-#####",
  TM: "+993-#-###-####",
  TN: "+216-##-###-###",
  TO: "+676-#####",
  TR: "+90(###)###-####",
  TT: "+1(868)###-####",
  TV: "+688-90####",
  TV: "+688-2####",
  TW: "+886-#-####-####",
  TW: "+886-####-####",
  TZ: "+255-##-###-####",
  UA: "+380(##)###-##-##",
  UG: "+256(###)###-###",
  UK: "+44-##-####-####",
  US: "+1(###)###-####",
  UY: "+598-#-###-##-##",
  UZ: "+998-##-###-####",
  VA: "+39-6-698-#####",
  VC: "+1(784)###-####",
  VE: "+58(###)###-####",
  VG: "+1(284)###-####",
  VI: "+1(340)###-####",
  VN: "+84-##-####-###",
  VN: "+84(###)####-###",
  VU: "+678-##-#####",
  VU: "+678-#####",
  WF: "+681-##-####",
  WS: "+685-##-####",
  YE: "+967-###-###-###",
  YE: "+967-#-###-###",
  YE: "+967-##-###-###",
  ZA: "+27-##-###-####",
  ZM: "+260-##-###-####",
  ZW: "+263-#-######"
}

export const tipoTabelas = [
  { label: 'TUSS', value: 'TUSS' },
  { label: 'AMB90', value: 'AMB90' },
  { label: 'AMB92', value: 'AMB92' },
  { label: 'AMB96', value: 'AMB96' },
  { label: 'AMB99', value: 'AMB99' },
  { label: 'CBHPM3', value: 'CBHPM3' },
  { label: 'CBHPM4', value: 'CBHPM4' },
  { label: 'CBHPM5', value: 'CBHPM5' },
  // { label: 'CBHPM2010', value: 'CBHPM2010' },
  // { label: 'CBHPM2012', value: 'CBHPM2012' },
  // { label: 'CBHPM2014', value: 'CBHPM2014' },
  // { label: 'CBHPM2016', value: 'CBHPM2016' },
  // { label: 'CBHPM2018', value: 'CBHPM2018' },
  // { label: 'CBHPM2020', value: 'CBHPM2020' },
  // { label: 'SUS', value: 'SUS' },
  { label: 'PRÓPRIA', value: 'PRÓPRIA' }
]

/**
 * Converte uma data UTC para o fuso horário do usuário
 * @param {string} utcDate - A data em formato UTC (ISO 8601)
 * @param {string} format - O formato desejado para a saída (padrão: 'dd/MM/yyyy HH:mm')
 * @returns {string} - A data formatada no fuso do usuário
 */
export function convertToUserTimeZone(utcDate, format = 'dd/MM/yyyy HH:mm') {
  const dateTime = DateTime.fromISO(utcDate, { zone: 'utc' }).setLocale("pt-BR").setZone('system')
  return format ? dateTime.toFormat(format) : dateTime.toISO()
}

export const docRequests = [
  "transcricao-exames",
  "validar-assinatura",
  "recebimento-imagens",
  "reprocessar-anexo",
  "reprocessar-anexo-provider",
  "send-pedido"
]

export const mascararCelular = (celular) => {
  if (celular === undefined || celular === null || celular === '') { return '' }

  const apenasNumeros = celular.replace(/\D/g, "") // Remove tudo que não for número

  let contador = 0

  const mascarado = celular.replace(/\d/g, () => {
    contador++
    const totalNumeros = apenasNumeros.length
    // Se estiver entre os últimos 4 números, mostra o número original
    return contador > totalNumeros - 4 ? apenasNumeros[contador - 1] : "*"
  })

  return mascarado
}

export const translateCrawlerStatus = (status) => {
  let badge
  switch (status) {
    case "AUTORIZADO":
      badge = <Badge color='success' pill>Autorizada</Badge>
      break
    case "AGUARDANDO_ANEXO":
      badge = <Badge color='light-secondary' pill>Aguardando anexo</Badge>
      break
    case "EM_ANALISE":
      badge = <Badge color='light-info' pill>Em análise</Badge>
      break
    case "NEGADO":
      badge = <Badge color='light-danger' pill>Negada</Badge>
      break
    case "PARCIALMENTE_AUTORIZADO":
      badge = <Badge color='light-success' pill>Parcialmente autorizada</Badge>
      break
    case "EXECUTADO":
      badge = <Badge color='success' pill>Executada</Badge>
      break
    case "PARCIALMENTE_EXECUTADO":
      badge = <Badge color='light-success' pill>Parcialmente executada</Badge>
      break
    case "AGUARDANDO_TOKEN":
      badge = <Badge color='light-secondary' pill>Aguardando token</Badge>
      break
    case "CANCELADO":
      badge = <Badge color='light-secondary' pill>Cancelado</Badge>
      break
    default:
      badge = <Badge color='light-info' pill>Inconsistência no convênio</Badge>
  }
  return badge
}

export const situacaoGuiaLabel = (codigo) => {
  switch ((codigo || '').toUpperCase()) {
    case 'A': return 'Autorizada'
    case 'B': return 'Aguardando Assinatura'
    case 'S': return 'Aguardando Anexo'
    case 'R': return 'Em Análise'
    case 'N': return 'Negada'
    case 'M': return 'Não Processada'
    case 'I': return 'Inconsistência no Convênio'
    case 'Z': return 'Parcialmente Autorizada'
    case 'H': return 'Aguardando Biometria'
    case 'T': return 'Biometria Inválida'
    case 'C': return 'Cancelada'
    case 'X': return 'Aguardando Cancelamento'
    case 'V': return 'Beneficiário Inválido'
    case 'W': return 'Aguardando Execução'
    case 'P': return 'Parcialmente Executada'
    case 'E': return 'Executada'
    case 'O': return 'Aguardando Token'
    case 'Ç': return 'Inconsistência no Convênio'
    default: return codigo || 'Inconsistência no Convênio'
  }
}

export const TIPO_GUIA = {
  Consulta: 'C',
  SADT: 'S',
  Internacao: 'I',
  Prorrogacao: 'P'
}

/**
 * Códigos do TipoGuia do SDK (Enuns/TipoGuia.cs).
 *
 * @param {string} codigo
 * @returns {string}
 */
export const tipoGuiaLabel = (codigo) => {
  switch ((codigo || '').toUpperCase()) {
    case 'C': return 'Consulta'
    case 'I': return 'Internação'
    case 'P': return 'Prorrogação'
    default: return 'SP/SADT'
  }
}

export const ehGuiaDeConsulta = (codigo) => (codigo || '').toUpperCase() === 'C'

export const boostrapDefaultColors = [
  'primary',
  'secondary',
  'success',
  'danger',
  'warning',
  'info',
  'dark'
]

export const getItemLoteStatuses = (status) => {
  switch (status) {
    case 'Enfileirado':
    case 0:
      return { color: 'light-info', text: 'Enfileirado' }
    case 'EmProgresso':
    case 1:
      return { color: 'light-info', text: 'Em Progresso' }
    case 'EmPausa':
    case 2:
      return { color: 'light-warning', text: 'Em Pausa' }
    case 'Concluido':
    case 3:
      return { color: 'success', text: 'Concluido' }
    default:
      return { color: 'light-info', text: 'Em Análise' }
  }
}

export const getLotesDeGuiasStatus = (status) => {
  switch (status) {
    case 'Concluido':
    case 6:
      return { color: 'success', text: 'Concluído' }
    case 'EmPausa':
    case 5:
      return { color: 'light-warning', text: 'Em Pausa' }
    case 'Enviando':
    case 4:
      return { color: 'light-info', text: 'Enviando' }
    case 'EmProgresso':
    case 3:
      return { color: 'light-info', text: 'Em Progresso' }
    case 'NaoIniciadoSemPendencias':
    case 2:
      return { color: 'light-info', text: 'Aguardando confirmação' }
    case 'NaoIniciadoComPendencias':
    case 1:
      return { color: 'light-warning', text: 'Pendências' }
    default:
      // 0, EmAnalise
      return { color: 'light-info', text: 'Em Análise' }
  }
}

const separarPartesAssinatura = valor => (valor || '').split('|').map(parte => parte.trim().toUpperCase()).filter(Boolean)

export const ROTULOS_PARTE_ASSINATURA = {
  BENEFICIARIO: 'Beneficiário',
  RESPONSAVEL: 'Responsável',
  CONTRATADO: 'Contratado',
  SOLICITANTE: 'Solicitante'
}

/**
 * @param {object} guia item da listagem do histórico
 * @returns {string[]} partes que podem assinar a guia e ainda não assinaram
 */
export const partesFaltantesAssinatura = (guia) => {
  if (!guia?.permitidoAssinar) return []
  const assinadas = new Set(separarPartesAssinatura(guia.assinadoPor))
  return separarPartesAssinatura(guia.partesPermitidasAssinar).filter(parte => !assinadas.has(parte))
}

/**
 * @param {object} guia item da listagem do histórico
 * @returns {string|null} ação que o usuário precisa fazer na guia, ou null quando não há pendência
 */
export const proximaAcaoGuia = (guia) => {
  if (!guia || guia.situacao === 'C' || guia.situacao === 'X') return null
  if (guia.podeInserirToken) return 'Inserir token'
  if (guia.situacao === 'S' && guia.podeAnexar) return 'Anexar documentos'
  if (guia.podeReprocessarBiometria) return 'Reprocessar biometria'
  if (guia.precisaExecutar) return 'Executar guia'
  if (partesFaltantesAssinatura(guia).length > 0) return 'Assinar guia'
  return null
}
